import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, where, writeBatch } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { getDateKeyChile } from '../../../../services/analyticsEventsService';
import { useAuth } from '../../../auth/components/AuthProvider';

const CONTACT_EVENT_TYPES = new Set(['contact_click', 'whatsapp_click', 'phone_click', 'email_click']);
const EMPTY_METRICS = {
  homeVisits: 0,
  searches: 0,
  profileVisits: 0,
  contactClicks: 0,
  uniqueCompaniesVisited: 0,
  tipVisits: 0,
};

function toDateSafe(ts) {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

function dateKeyShift(days) {
  const now = new Date();
  const d = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const pick = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

function getRange(range) {
  const today = getDateKeyChile();
  if (range === 'today') return { start: today, end: today, isAll: false, label: 'Hoy' };
  if (range === 'yesterday') {
    const y = dateKeyShift(-1);
    return { start: y, end: y, isAll: false, label: 'Ayer' };
  }
  if (range === '7d') return { start: dateKeyShift(-6), end: today, isAll: false, label: 'Últimos 7 días' };
  if (range === '30d') return { start: dateKeyShift(-29), end: today, isAll: false, label: 'Últimos 30 días' };
  return { start: null, end: null, isAll: true, label: 'Acumulado' };
}

function sumDailyDocs(docs) {
  const out = { ...EMPTY_METRICS };
  docs.forEach((d) => {
    const x = d.data ? d.data() : d;
    out.homeVisits += Number(x?.homeVisits || 0);
    out.searches += Number(x?.searches || 0);
    out.profileVisits += Number(x?.profileVisits || 0);
    out.contactClicks += Number(x?.contactClicks || 0);
    out.uniqueCompaniesVisited += Number(x?.uniqueCompaniesVisited || 0);
    out.tipVisits += Number(x?.tipVisits || 0);
  });
  return out;
}

function eventTypeLabel(type) {
  switch (type) {
    case 'home_visit': return 'Visita Home';
    case 'search': return 'Búsqueda';
    case 'profile_visit': return 'Visita perfil';
    case 'contact_click': return 'Click contacto';
    case 'whatsapp_click': return 'Click WhatsApp';
    case 'phone_click': return 'Click teléfono';
    case 'email_click': return 'Click correo';
    case 'tip_view': return 'Visita tip';
    default: return type || 'Evento';
  }
}

function formatHour(ts) {
  const d = toDateSafe(ts);
  if (!d) return '--:--';
  return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
}

function computeDelta(current, previous) {
  const cur = Number(current || 0);
  const prev = Number(previous || 0);
  const abs = cur - prev;
  if (prev <= 0) {
    if (cur <= 0) return { pct: 0, abs, trend: 'neutral' };
    return { pct: 100, abs, trend: 'up' };
  }
  const pct = Number((((cur - prev) / prev) * 100).toFixed(1));
  if (pct > 0) return { pct, abs, trend: 'up' };
  if (pct < 0) return { pct, abs, trend: 'down' };
  return { pct: 0, abs, trend: 'neutral' };
}

function trendLabel(trend) {
  if (trend === 'up') return 'Sube';
  if (trend === 'down') return 'Baja';
  return 'Igual';
}

function TrendBadge({ trend, text }) {
  const cls =
    trend === 'up'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : trend === 'down'
        ? 'bg-rose-50 text-rose-700 border-rose-200'
        : 'bg-slate-50 text-slate-600 border-slate-200';
  const icon = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→';
  return <span className={`inline-flex items-center gap-1 text-[11px] border rounded-full px-2 py-0.5 ${cls}`}>{icon} {text}</span>;
}

function percentSafe(num, den) {
  const n = Number(num || 0);
  const d = Number(den || 0);
  if (!d) return 0;
  return Number(((n / d) * 100).toFixed(1));
}

function compactNumber(v) {
  return Number(v || 0).toLocaleString('es-CL');
}

function isPermissionDeniedError(err) {
  return err?.code === 'permission-denied' || String(err?.message || '').toLowerCase().includes('permission');
}

export default function AdminAudienciaActividad() {
  const { user, rol, loading: authLoading } = useAuth();
  const [range, setRange] = useState('today');
  const [viewMode, setViewMode] = useState('executive');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [todayMetrics, setTodayMetrics] = useState({ ...EMPTY_METRICS });
  const [yesterdayMetrics, setYesterdayMetrics] = useState({ ...EMPTY_METRICS });
  const [rangeMetrics, setRangeMetrics] = useState({ ...EMPTY_METRICS });
  const [accumMetrics, setAccumMetrics] = useState({ ...EMPTY_METRICS });
  const [recentEvents, setRecentEvents] = useState([]);
  const [eventsRange, setEventsRange] = useState([]);
  const [topEmpresasToday, setTopEmpresasToday] = useState([]);
  const [topTipsToday, setTopTipsToday] = useState([]);
  const [companyNames, setCompanyNames] = useState({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPurging, setIsPurging] = useState(false);
  const canReadAnalytics = !!user && ['admin', 'agente'].includes(String(rol || '').toLowerCase());

  const resolveCompanyNames = useCallback(async (ids) => {
    const uniq = [...new Set(ids.filter(Boolean))];
    if (uniq.length === 0) return {};
    const entries = await Promise.all(
      uniq.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, 'empresas', id));
          if (!snap.exists()) return [id, id];
          const n = snap.data()?.nombre || id;
          return [id, n];
        } catch {
          return [id, id];
        }
      })
    );
    return Object.fromEntries(entries);
  }, []);

  const loadData = useCallback(async () => {
    if (authLoading) return;
    if (!canReadAnalytics) {
      setLoading(false);
      setError('Sin acceso a analytics: inicia sesión con rol admin/agente para ver este panel.');
      setTodayMetrics({ ...EMPTY_METRICS });
      setYesterdayMetrics({ ...EMPTY_METRICS });
      setRangeMetrics({ ...EMPTY_METRICS });
      setAccumMetrics({ ...EMPTY_METRICS });
      setRecentEvents([]);
      setEventsRange([]);
      setTopEmpresasToday([]);
      setTopTipsToday([]);
      setCompanyNames({});
      return;
    }
    setLoading(true);
    setError('');
    const todayKey = getDateKeyChile();
    const yesterdayKey = dateKeyShift(-1);
    const selected = getRange(range);

    try {
      const todaySnap = await getDoc(doc(db, 'analytics_daily', todayKey));
      const yesterdaySnap = await getDoc(doc(db, 'analytics_daily', yesterdayKey));
      const todayBase = todaySnap.exists() ? sumDailyDocs([todaySnap]) : { ...EMPTY_METRICS };
      const yesterdayBase = yesterdaySnap.exists() ? sumDailyDocs([yesterdaySnap]) : { ...EMPTY_METRICS };

      let allDailyDocs = [];
      try {
        const allDailySnap = await getDocs(query(collection(db, 'analytics_daily'), orderBy('dateKey', 'desc'), limit(2000)));
        allDailyDocs = allDailySnap.docs;
      } catch {
        const allDailySnap = await getDocs(collection(db, 'analytics_daily'));
        allDailyDocs = allDailySnap.docs;
      }
      setAccumMetrics(sumDailyDocs(allDailyDocs));

      let rangeDailyDocs = [];
      if (selected.isAll) {
        rangeDailyDocs = allDailyDocs;
      } else {
        try {
          const rangeDailySnap = await getDocs(
            query(
              collection(db, 'analytics_daily'),
              where('dateKey', '>=', selected.start),
              where('dateKey', '<=', selected.end),
              orderBy('dateKey', 'asc')
            )
          );
          rangeDailyDocs = rangeDailySnap.docs;
        } catch {
          const snap = await getDocs(collection(db, 'analytics_daily'));
          rangeDailyDocs = snap.docs.filter((d) => {
            const key = d.data()?.dateKey || d.id;
            return key >= selected.start && key <= selected.end;
          });
        }
      }
      setRangeMetrics(sumDailyDocs(rangeDailyDocs));

      // Eventos de hoy (reciente + cálculos finos)
      let todayEvents = [];
      try {
        const recentSnap = await getDocs(query(collection(db, 'analytics_events'), where('dateKey', '==', todayKey), orderBy('createdAt', 'desc'), limit(400)));
        todayEvents = recentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch {
        const recentSnap = await getDocs(query(collection(db, 'analytics_events'), where('dateKey', '==', todayKey), limit(600)));
        todayEvents = recentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        todayEvents.sort((a, b) => (toDateSafe(b.createdAt)?.getTime() || 0) - (toDateSafe(a.createdAt)?.getTime() || 0));
      }
      setRecentEvents(todayEvents.slice(0, 120));

      const uniqueCompaniesToday = new Set(
        todayEvents.filter((e) => e.type === 'profile_visit' && e.empresaId).map((e) => e.empresaId)
      ).size;
      setTodayMetrics({ ...todayBase, uniqueCompaniesVisited: uniqueCompaniesToday });
      setYesterdayMetrics(yesterdayBase);

      // Eventos del rango para tablas (searches)
      let rangeEventsRaw = [];
      if (selected.isAll) {
        const allEventsSnap = await getDocs(query(collection(db, 'analytics_events'), orderBy('createdAt', 'desc'), limit(3500)));
        rangeEventsRaw = allEventsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } else {
        try {
          const rangeEventsSnap = await getDocs(
            query(
              collection(db, 'analytics_events'),
              where('dateKey', '>=', selected.start),
              where('dateKey', '<=', selected.end),
              orderBy('dateKey', 'desc'),
              limit(3500)
            )
          );
          rangeEventsRaw = rangeEventsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch {
          const allEventsSnap = await getDocs(query(collection(db, 'analytics_events'), orderBy('createdAt', 'desc'), limit(3500)));
          rangeEventsRaw = allEventsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((e) => {
              const key = e.dateKey || '';
              return key >= selected.start && key <= selected.end;
            });
        }
      }
      setEventsRange(rangeEventsRaw);

      // Top empresas hoy
      let topEmpresas = [];
      try {
        const topSnap = await getDocs(query(collection(db, 'analytics_daily', todayKey, 'empresas'), orderBy('profileVisits', 'desc'), limit(50)));
        topEmpresas = topSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch {
        const byCompany = {};
        todayEvents.forEach((e) => {
          if (!e.empresaId) return;
          if (!byCompany[e.empresaId]) {
            byCompany[e.empresaId] = {
              empresaId: e.empresaId,
              empresaNombre: e.empresaNombre || null,
              profileVisits: 0,
              contactClicks: 0,
              lastVisitAt: null,
            };
          }
          if (e.type === 'profile_visit') byCompany[e.empresaId].profileVisits += 1;
          if (CONTACT_EVENT_TYPES.has(e.type)) byCompany[e.empresaId].contactClicks += 1;
          const t = toDateSafe(e.createdAt);
          if (t && (!byCompany[e.empresaId].lastVisitAt || t > toDateSafe(byCompany[e.empresaId].lastVisitAt))) {
            byCompany[e.empresaId].lastVisitAt = e.createdAt;
          }
        });
        topEmpresas = Object.values(byCompany);
      }
      topEmpresas.sort((a, b) => (b.profileVisits || 0) - (a.profileVisits || 0));
      setTopEmpresasToday(topEmpresas.slice(0, 30));

      let topTips = [];
      try {
        const tipsSnap = await getDocs(
          query(collection(db, 'analytics_daily', todayKey, 'tips'), orderBy('visits', 'desc'), limit(40))
        );
        topTips = tipsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch {
        const byTip = {};
        todayEvents.forEach((e) => {
          if (e.type !== 'tip_view' || !e.tipId) return;
          if (!byTip[e.tipId]) {
            byTip[e.tipId] = {
              tipId: e.tipId,
              tipSlug: e.tipSlug || null,
              tipTitulo: e.tipTitulo || null,
              visits: 0,
            };
          }
          byTip[e.tipId].visits += 1;
        });
        topTips = Object.values(byTip);
      }
      topTips.sort((a, b) => (Number(b.visits) || 0) - (Number(a.visits) || 0));
      setTopTipsToday(topTips.slice(0, 30));

      // Resolver nombres de empresa faltantes
      const allCompanyIds = [
        ...topEmpresas.map((e) => e.empresaId),
        ...todayEvents.map((e) => e.empresaId),
      ].filter(Boolean);
      const nameMap = await resolveCompanyNames(allCompanyIds);
      setCompanyNames(nameMap);

      setLastUpdated(new Date());
    } catch (err) {
      if (isPermissionDeniedError(err)) {
        setError('No tienes permisos para leer analytics_* en Firestore. Solicita habilitar lectura admin para esta sección.');
      } else {
        console.error('[AdminAudienciaActividad] error', err);
        setError('No se pudo cargar la audiencia. Revisa permisos/índices de analytics_*.');
      }
      // Dejar el panel operativo con métricas en cero cuando no hay permisos/datos.
      setTodayMetrics({ ...EMPTY_METRICS });
      setYesterdayMetrics({ ...EMPTY_METRICS });
      setRangeMetrics({ ...EMPTY_METRICS });
      setAccumMetrics({ ...EMPTY_METRICS });
      setRecentEvents([]);
      setEventsRange([]);
      setTopEmpresasToday([]);
      setTopTipsToday([]);
      setCompanyNames({});
    } finally {
      setLoading(false);
    }
  }, [authLoading, canReadAnalytics, range, resolveCompanyNames]);

  const deleteByBatches = useCallback(async (docs) => {
    let total = 0;
    for (let i = 0; i < docs.length; i += 400) {
      const slice = docs.slice(i, i + 400);
      const batch = writeBatch(db);
      slice.forEach((d) => batch.delete(d.ref));
      await batch.commit();
      total += slice.length;
    }
    return total;
  }, []);

  const handlePurgeKpiData = useCallback(async () => {
    if (!canReadAnalytics) {
      setError('No tienes permisos admin/agente para ejecutar esta eliminación.');
      return;
    }
    const ok = window.confirm(
      'Se eliminarán los datos de KPI y actividad (audiencia, visitas de perfil y búsquedas) hasta hoy. Esta acción no se puede deshacer. ¿Deseas continuar?'
    );
    if (!ok) return;
    const token = window.prompt('Para confirmar, escribe ELIMINAR');
    if (token !== 'ELIMINAR') return;

    setIsPurging(true);
    setError('');
    const todayKey = getDateKeyChile();
    try {
      // 1) analytics_daily/{dateKey}/empresas/*
      const dailySnap = await getDocs(query(collection(db, 'analytics_daily'), where('dateKey', '<=', todayKey)));
      let deletedDailyCompanyDocs = 0;
      let deletedDailyTipDocs = 0;
      for (const dayDoc of dailySnap.docs) {
        const companiesSnap = await getDocs(collection(db, 'analytics_daily', dayDoc.id, 'empresas'));
        deletedDailyCompanyDocs += await deleteByBatches(companiesSnap.docs);
        const tipsSnap = await getDocs(collection(db, 'analytics_daily', dayDoc.id, 'tips'));
        deletedDailyTipDocs += await deleteByBatches(tipsSnap.docs);
      }

      // 2) analytics_daily/{dateKey}
      const deletedDailyDocs = await deleteByBatches(dailySnap.docs);

      // 3) analytics_events (hasta hoy)
      const eventsSnap = await getDocs(query(collection(db, 'analytics_events'), where('dateKey', '<=', todayKey), limit(10000)));
      const deletedEvents = await deleteByBatches(eventsSnap.docs);

      // 4) Legacy para visitas/búsquedas históricas del admin
      const legacyCollections = ['visitas_perfil_empresa', 'search_logs', 'busquedas_homepage'];
      let deletedLegacy = 0;
      for (const colName of legacyCollections) {
        const snap = await getDocs(query(collection(db, colName), limit(10000)));
        deletedLegacy += await deleteByBatches(snap.docs);
      }

      await loadData();
      window.alert(
        `Limpieza completada.\nanalytics_events: ${deletedEvents}\nanalytics_daily: ${deletedDailyDocs}\nanalytics_daily/*/empresas: ${deletedDailyCompanyDocs}\nanalytics_daily/*/tips: ${deletedDailyTipDocs}\nlegacy: ${deletedLegacy}`
      );
    } catch (err) {
      const message = isPermissionDeniedError(err)
        ? 'No tienes permisos para eliminar analytics/visitas/búsquedas. Revisa reglas de Firestore para rol admin.'
        : 'No se pudo completar la eliminación de datos KPI.';
      setError(message);
      console.error('[AdminAudienciaActividad] purge error', err);
    } finally {
      setIsPurging(false);
    }
  }, [canReadAnalytics, deleteByBatches, loadData]);

  useEffect(() => {
    loadData();
    const id = setInterval(loadData, 45000);
    return () => clearInterval(id);
  }, [loadData]);

  const conversionHoy = useMemo(() => percentSafe(todayMetrics.contactClicks, todayMetrics.profileVisits), [todayMetrics]);
  const conversionAyer = useMemo(() => percentSafe(yesterdayMetrics.contactClicks, yesterdayMetrics.profileVisits), [yesterdayMetrics]);

  const topTipsInRange = useMemo(() => {
    const map = {};
    eventsRange.forEach((e) => {
      if (e.type !== 'tip_view' || !e.tipId) return;
      if (!map[e.tipId]) {
        map[e.tipId] = {
          tipId: e.tipId,
          tipSlug: e.tipSlug || '',
          tipTitulo: e.tipTitulo || '',
          count: 0,
        };
      }
      map[e.tipId].count += 1;
      if (!map[e.tipId].tipTitulo && e.tipTitulo) map[e.tipId].tipTitulo = e.tipTitulo;
      if (!map[e.tipId].tipSlug && e.tipSlug) map[e.tipId].tipSlug = e.tipSlug;
    });
    return Object.values(map).sort((a, b) => b.count - a.count || (a.tipTitulo || '').localeCompare(b.tipTitulo || '', 'es'));
  }, [eventsRange]);

  const searchesByText = useMemo(() => {
    const map = {};
    eventsRange.forEach((e) => {
      if (e.type !== 'search') return;
      const key = String(e.searchText || '').trim().toLowerCase();
      if (!key) return;
      if (!map[key]) map[key] = { searchText: e.searchText || key, count: 0, lastAt: e.createdAt || null };
      map[key].count += 1;
      const prev = toDateSafe(map[key].lastAt)?.getTime() || 0;
      const curr = toDateSafe(e.createdAt)?.getTime() || 0;
      if (curr > prev) map[key].lastAt = e.createdAt;
    });
    return Object.values(map).sort((a, b) => b.count - a.count || (toDateSafe(b.lastAt)?.getTime() || 0) - (toDateSafe(a.lastAt)?.getTime() || 0));
  }, [eventsRange]);

  const hourlyToday = useMemo(() => {
    const base = Array.from({ length: 24 }).map((_, h) => ({
      hour: `${String(h).padStart(2, '0')}:00`,
      home: 0,
      search: 0,
      profile: 0,
      contact: 0,
      tip: 0,
    }));
    recentEvents.forEach((e) => {
      const d = toDateSafe(e.createdAt);
      if (!d) return;
      const h = Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Santiago', hour: '2-digit', hourCycle: 'h23' }).format(d));
      if (Number.isNaN(h) || h < 0 || h > 23) return;
      if (e.type === 'home_visit') base[h].home += 1;
      else if (e.type === 'search') base[h].search += 1;
      else if (e.type === 'profile_visit') base[h].profile += 1;
      else if (e.type === 'tip_view') base[h].tip += 1;
      else if (CONTACT_EVENT_TYPES.has(e.type)) base[h].contact += 1;
    });
    return base;
  }, [recentEvents]);

  const maxHourTotal = useMemo(
    () => Math.max(1, ...hourlyToday.map((h) => h.home + h.search + h.profile + h.contact + h.tip)),
    [hourlyToday]
  );

  const funnel = useMemo(() => {
    const home = Number(todayMetrics.homeVisits || 0);
    const search = Number(todayMetrics.searches || 0);
    const profile = Number(todayMetrics.profileVisits || 0);
    const contact = Number(todayMetrics.contactClicks || 0);
    return {
      home,
      search,
      profile,
      contact,
      homeToSearch: percentSafe(search, home),
      searchToProfile: percentSafe(profile, search),
      profileToContact: percentSafe(contact, profile),
    };
  }, [todayMetrics]);

  const topEmpresasRows = useMemo(() => {
    return topEmpresasToday.map((e) => {
      const empresaId = e.empresaId || e.id || '';
      const name = e.empresaNombre || companyNames[empresaId] || null;
      return {
        ...e,
        empresaId,
        displayName: name || 'Empresa sin nombre',
        conversion: percentSafe(e.contactClicks || 0, e.profileVisits || 0),
      };
    });
  }, [topEmpresasToday, companyNames]);

  const kpiCards = [
    { key: 'homeVisits', title: 'Visitas Home hoy', today: todayMetrics.homeVisits, yesterday: yesterdayMetrics.homeVisits },
    { key: 'searches', title: 'Búsquedas hoy', today: todayMetrics.searches, yesterday: yesterdayMetrics.searches },
    { key: 'profileVisits', title: 'Visitas a perfiles hoy', today: todayMetrics.profileVisits, yesterday: yesterdayMetrics.profileVisits },
    { key: 'contactClicks', title: 'Contactos hoy', today: todayMetrics.contactClicks, yesterday: yesterdayMetrics.contactClicks },
    { key: 'conversion', title: 'Conversión perfil → contacto hoy', today: conversionHoy, yesterday: conversionAyer, suffix: '%' },
    { key: 'uniqueCompaniesVisited', title: 'Empresas únicas visitadas hoy', today: todayMetrics.uniqueCompaniesVisited, yesterday: yesterdayMetrics.uniqueCompaniesVisited || 0 },
    { key: 'tipVisits', title: 'Visitas a tips hoy', today: todayMetrics.tipVisits, yesterday: yesterdayMetrics.tipVisits || 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">KPIs de Audiencia y Conversión</h1>
          <p className="text-slate-600 text-sm mt-1">
            Vista ejecutiva y operativa: hoy, comparación contra ayer, embudo y ranking de empresas.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handlePurgeKpiData}
            disabled={isPurging}
            className={`px-3 py-1.5 rounded-lg text-sm border ${
              isPurging
                ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
            }`}
            title="Elimina datos KPI y actividad hasta hoy"
          >
            {isPurging ? 'Eliminando datos...' : 'Eliminar datos KPI'}
          </button>
          <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden bg-white">
            <button
              type="button"
              onClick={() => setViewMode('executive')}
              className={`px-3 py-1.5 text-sm ${viewMode === 'executive' ? 'bg-slate-800 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              Vista ejecutiva
            </button>
            <button
              type="button"
              onClick={() => setViewMode('operational')}
              className={`px-3 py-1.5 text-sm border-l border-slate-300 ${viewMode === 'operational' ? 'bg-slate-800 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              Vista operativa
            </button>
          </div>
          {[
            { id: 'today', label: 'Hoy' },
            { id: 'yesterday', label: 'Ayer' },
            { id: '7d', label: 'Últimos 7 días' },
            { id: '30d', label: 'Últimos 30 días' },
            { id: 'all', label: 'Acumulado' },
          ].map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setRange(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm border ${
                range === f.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl p-3 text-sm">{error}</div>}

      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
        {kpiCards.map((k) => {
          const delta = computeDelta(k.today, k.yesterday);
          return (
            <div key={k.key} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="text-xs text-slate-500">{k.title}</div>
              <div className="text-2xl font-bold text-slate-900 mt-1">
                {k.suffix ? `${Number(k.today || 0).toFixed(1)}${k.suffix}` : compactNumber(k.today)}
              </div>
              <div className="mt-2 text-xs text-slate-600 space-y-1">
                <div>Hoy: <strong>{k.suffix ? `${Number(k.today || 0).toFixed(1)}${k.suffix}` : compactNumber(k.today)}</strong></div>
                <div>Ayer: <strong>{k.suffix ? `${Number(k.yesterday || 0).toFixed(1)}${k.suffix}` : compactNumber(k.yesterday)}</strong></div>
                <div>Diferencia: <strong>{k.suffix ? `${delta.abs >= 0 ? '+' : ''}${delta.abs.toFixed(1)}${k.suffix}` : `${delta.abs >= 0 ? '+' : ''}${compactNumber(delta.abs)}`}</strong></div>
                <TrendBadge trend={delta.trend} text={`${delta.pct >= 0 ? '+' : ''}${delta.pct}%`} />
              </div>
            </div>
          );
        })}
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-4">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Hoy vs Ayer</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-2 pr-4">KPI</th>
                <th className="py-2 pr-4">Hoy</th>
                <th className="py-2 pr-4">Ayer</th>
                <th className="py-2 pr-4">Variación %</th>
                <th className="py-2">Tendencia</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Visitas Home', hoy: todayMetrics.homeVisits, ayer: yesterdayMetrics.homeVisits },
                { label: 'Búsquedas', hoy: todayMetrics.searches, ayer: yesterdayMetrics.searches },
                { label: 'Visitas Perfil', hoy: todayMetrics.profileVisits, ayer: yesterdayMetrics.profileVisits },
                { label: 'Contactos', hoy: todayMetrics.contactClicks, ayer: yesterdayMetrics.contactClicks },
                { label: 'Visitas tips', hoy: todayMetrics.tipVisits, ayer: yesterdayMetrics.tipVisits || 0 },
                { label: 'Conversión', hoy: conversionHoy, ayer: conversionAyer, suffix: '%' },
              ].map((r) => {
                const delta = computeDelta(r.hoy, r.ayer);
                return (
                  <tr key={r.label} className="border-b border-slate-100">
                    <td className="py-2 pr-4">{r.label}</td>
                    <td className="py-2 pr-4">{r.suffix ? `${r.hoy.toFixed(1)}%` : compactNumber(r.hoy)}</td>
                    <td className="py-2 pr-4">{r.suffix ? `${r.ayer.toFixed(1)}%` : compactNumber(r.ayer)}</td>
                    <td className="py-2 pr-4">{delta.pct >= 0 ? '+' : ''}{delta.pct}%</td>
                    <td className="py-2"><TrendBadge trend={delta.trend} text={trendLabel(delta.trend)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-4">
        

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Tips más visitados ({getRange(range).label})</h2>
          {topTipsInRange.length === 0 ? (
            <p className="text-sm text-slate-500">Sin datos de tips en el período seleccionado.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-2 pr-3">Tip</th>
                    <th className="py-2 pr-3">Slug</th>
                    <th className="py-2 pr-3">Visitas</th>
                    <th className="py-2">ID</th>
                  </tr>
                </thead>
                <tbody>
                  {topTipsInRange.slice(0, 25).map((t) => (
                    <tr key={t.tipId} className="border-b border-slate-100">
                      <td className="py-2 pr-3 font-medium text-slate-800">{t.tipTitulo || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600">{t.tipSlug || '—'}</td>
                      <td className="py-2 pr-3 font-semibold">{compactNumber(t.count)}</td>
                      <td className="py-2 text-[11px] text-slate-400">{t.tipId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Embudo del día</h2>
          <div className="space-y-3">
            {[
              { label: 'Home', value: funnel.home },
              { label: 'Búsquedas', value: funnel.search },
              { label: 'Visitas a perfil', value: funnel.profile },
              { label: 'Contactos', value: funnel.contact },
            ].map((s, idx) => (
              <div key={s.label} className="flex items-center gap-3">
                <div className="w-28 text-xs text-slate-600">{s.label}</div>
                <div className="flex-1 h-2.5 bg-slate-100 rounded">
                  <div className="h-full bg-blue-500 rounded" style={{ width: `${Math.max(3, Math.round((s.value / Math.max(1, funnel.home)) * 100))}%` }} />
                </div>
                <div className="w-20 text-right text-sm font-semibold text-slate-800">{compactNumber(s.value)}</div>
                {idx < 3 && <div className="text-xs text-slate-500 ml-2">→</div>}
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">Home → Búsqueda: <strong>{funnel.homeToSearch}%</strong></div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">Búsqueda → Perfil: <strong>{funnel.searchToProfile}%</strong></div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">Perfil → Contacto: <strong>{funnel.profileToContact}%</strong></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Actividad por hora (hoy)</h2>
          <div className="space-y-2 max-h-[360px] overflow-auto pr-1">
            {hourlyToday.map((h) => {
              const total = h.home + h.search + h.profile + h.contact + h.tip;
              const widthPct = Math.min(100, Math.round((total / maxHourTotal) * 100));
              return (
                <div key={h.hour} className="grid grid-cols-[52px_1fr_220px] gap-2 items-center">
                  <div className="text-[11px] text-slate-500">{h.hour}</div>
                  <div className="h-2.5 rounded bg-slate-100 overflow-hidden">
                    <div className="h-full bg-indigo-500" style={{ width: `${widthPct}%` }} />
                  </div>
                  <div className="text-[11px] text-slate-600">H:{h.home} · B:{h.search} · P:{h.profile} · T:{h.tip} · C:{h.contact}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {viewMode === 'operational' && (
        <>
          <section className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-3">Top empresas del día</h2>
            {topEmpresasRows.length === 0 ? (
              <p className="text-sm text-slate-500">Sin visitas de perfiles hoy.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="text-left text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="py-2 pr-3">Empresa</th>
                      <th className="py-2 pr-3">Visitas hoy</th>
                      <th className="py-2 pr-3">Contactos hoy</th>
                      <th className="py-2 pr-3">Conversión</th>
                      <th className="py-2 pr-3">Última visita</th>
                      <th className="py-2">ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topEmpresasRows.map((e) => (
                      <tr key={e.empresaId || e.id} className="border-b border-slate-100">
                        <td className="py-2 pr-3 font-medium text-slate-800">{e.displayName}</td>
                        <td className="py-2 pr-3">{compactNumber(e.profileVisits)}</td>
                        <td className="py-2 pr-3">{compactNumber(e.contactClicks)}</td>
                        <td className="py-2 pr-3">{e.conversion.toFixed(1)}%</td>
                        <td className="py-2 pr-3">{formatHour(e.lastVisitAt)}</td>
                        <td className="py-2 text-[11px] text-slate-400">{e.empresaId || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">Actividad reciente (hoy)</h2>
              {loading ? (
                <p className="text-sm text-slate-500">Cargando actividad...</p>
              ) : recentEvents.length === 0 ? (
                <p className="text-sm text-slate-500">Sin actividad registrada hoy.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="py-2 pr-4">Hora</th>
                        <th className="py-2 pr-4">Tipo</th>
                        <th className="py-2 pr-4">Empresa</th>
                        <th className="py-2 pr-4">Tip</th>
                        <th className="py-2">Búsqueda</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentEvents.slice(0, 50).map((e) => {
                        const eid = e.empresaId || '';
                        const name = e.empresaNombre || companyNames[eid] || '-';
                        const tipLabel = e.type === 'tip_view' ? e.tipTitulo || e.tipSlug || e.tipId || '-' : '-';
                        return (
                          <tr key={e.id} className="border-b border-slate-100">
                            <td className="py-2 pr-4">{formatHour(e.createdAt)}</td>
                            <td className="py-2 pr-4">{eventTypeLabel(e.type)}</td>
                            <td className="py-2 pr-4">{name}</td>
                            <td className="py-2 pr-4 max-w-[200px] truncate" title={tipLabel}>{tipLabel}</td>
                            <td className="py-2">{e.searchText || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">Búsquedas ({getRange(range).label})</h2>
              {searchesByText.length === 0 ? (
                <p className="text-sm text-slate-500">Sin búsquedas para el filtro seleccionado.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="py-2 pr-3">Texto buscado</th>
                        <th className="py-2 pr-3">Cantidad</th>
                        <th className="py-2">Última hora</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchesByText.slice(0, 30).map((s) => (
                        <tr key={`${s.searchText}_${s.count}`} className="border-b border-slate-100">
                          <td className="py-2 pr-3">{s.searchText}</td>
                          <td className="py-2 pr-3 font-semibold">{s.count}</td>
                          <td className="py-2">{formatHour(s.lastAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Home acumulado</div>
          <div className="text-2xl font-bold text-slate-900">{compactNumber(accumMetrics.homeVisits)}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Búsquedas acumuladas</div>
          <div className="text-2xl font-bold text-slate-900">{compactNumber(accumMetrics.searches)}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Perfiles acumulados</div>
          <div className="text-2xl font-bold text-slate-900">{compactNumber(accumMetrics.profileVisits)}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Contactos acumulados</div>
          <div className="text-2xl font-bold text-slate-900">{compactNumber(accumMetrics.contactClicks)}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Visitas tips acumuladas</div>
          <div className="text-2xl font-bold text-slate-900">{compactNumber(accumMetrics.tipVisits)}</div>
        </div>
      </section>

      <section className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
        <div><strong>Filtro activo:</strong> {getRange(range).label}</div>
        <div className="mt-1">
          <strong>Rango:</strong> Home {compactNumber(rangeMetrics.homeVisits)} · Búsquedas {compactNumber(rangeMetrics.searches)} · Perfiles {compactNumber(rangeMetrics.profileVisits)} · Tips {compactNumber(rangeMetrics.tipVisits)} · Contactos {compactNumber(rangeMetrics.contactClicks)}
        </div>
        <div className="mt-1 text-xs text-slate-500">{lastUpdated ? `Actualizado ${lastUpdated.toLocaleString('es-CL')}` : ''}</div>
      </section>
    </div>
  );
}

